import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Textarea,
  Button,
  Avatar,
  useColorModeValue,
  Divider,
  IconButton,
  useToast,
  Badge,
  Spacer,
  Spinner,
  Center,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure
} from '@chakra-ui/react';
import { FiThumbsUp, FiThumbsDown, FiCornerUpLeft, FiTrash2 } from 'react-icons/fi';
import IconWrapper from './IconWrapper';
import { useAuth } from '../contexts/AuthContext';
import { addComment, getReplies, deleteComment, Comment as FirebaseComment } from '../services/commentsService';
import { onSnapshot, query, collection, where, orderBy, limit } from 'firebase/firestore';
import { db } from '../lib/firebase';

interface Comment {
  id: string;
  author: string;
  avatar?: string;
  content: string;
  timestamp: string;
  likes: number;
  dislikes: number;
  replies: Comment[];
  isLiked?: boolean;
  isDisliked?: boolean;
  userId?: string;
}

interface CommentSectionProps {
  contentId: string;
  contentCreatorId?: string;
  contentTitle?: string;
}

const CommentSection: React.FC<CommentSectionProps> = ({ contentId, contentCreatorId, contentTitle }) => {
  const { user } = useAuth();
  const [newComment, setNewComment] = useState('');
  const [comments, setComments] = useState<Comment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState<string | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);

  const toast = useToast();
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.900', 'white');
  const placeholderColor = useColorModeValue('gray.500', 'gray.400');
  const inputBg = useColorModeValue('gray.50', 'gray.700');

  // Load comments from Firebase
  useEffect(() => {
    if (!contentId) return;

    const q = query(
      collection(db, 'comments'),
      where('contentId', '==', contentId),
      orderBy('createdAt', 'desc'),
      limit(50)
    );

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      const commentsData: Comment[] = [];
      
      for (const doc of snapshot.docs) {
        const data = doc.data() as FirebaseComment;
        
        // Only process top-level comments (no parentId or parentId is null/undefined)
        if (!data.parentId) {
          // Get replies for this comment
          const replies = await getReplies(doc.id);
          const repliesData: Comment[] = replies.map(reply => ({
            id: reply.id || '',
            author: reply.userName,
            avatar: reply.userAvatar,
            content: reply.text,
            timestamp: formatTimestamp(reply.createdAt),
            likes: reply.likes || 0,
            dislikes: reply.dislikes || 0,
            replies: [],
            userId: reply.userId
          }));

          commentsData.push({
            id: doc.id,
            author: data.userName,
            avatar: data.userAvatar,
            content: data.text,
            timestamp: formatTimestamp(data.createdAt),
            likes: data.likes || 0,
            dislikes: data.dislikes || 0,
            replies: repliesData,
            userId: data.userId
          });
        }
      }
      
      setComments(commentsData);
      setIsLoading(false);
    }, (error) => {
      console.error('Error loading comments:', error);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [contentId]);

  const formatTimestamp = (timestamp: any): string => {
    if (!timestamp) return 'Just now';
    
    const now = new Date();
    const commentDate = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const diffInSeconds = Math.floor((now.getTime() - commentDate.getTime()) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return `${Math.floor(diffInSeconds / 86400)}d ago`;
  };

  const handleSubmitComment = async () => {
    if (!newComment.trim()) {
      toast({
        title: 'Empty Comment',
        description: 'Please write something before posting.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!user) {
      toast({
        title: 'Please Sign In',
        description: 'You need to be signed in to post comments.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      await addComment(
        contentId,
        user.uid,
        user.displayName || 'Anonymous',
        user.photoURL || undefined,
        newComment.trim()
      );
      
      // Create notification for content creator (if not the creator themselves)
      if (contentCreatorId && user.uid !== contentCreatorId) {
        try {
          const { NotificationService } = await import('../services/notificationService');
          await NotificationService.createNotification(
            contentCreatorId,
            'comment',
            'New Comment',
            `${user.displayName || 'Someone'} commented on your content "${contentTitle || 'content'}"`,
            `/content/${contentId}`,
            { contentId, userId: user.uid }
          );
        } catch (error) {
          console.error('Error creating notification:', error);
          // Don't show error to user - notification is non-critical
        }
      }
      
      setNewComment('');
      
      toast({
        title: 'Comment Posted',
        description: 'Your comment has been added successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error posting comment:', error);
      toast({
        title: 'Error',
        description: 'Failed to post comment. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLike = (commentId: string) => {
    // TODO: Implement Firebase like functionality
    setComments(comments.map(comment => {
      if (comment.id === commentId) {
        const wasLiked = comment.isLiked;
        const wasDisliked = comment.isDisliked;
        
        return {
          ...comment,
          isLiked: !wasLiked,
          isDisliked: false,
          likes: wasLiked ? comment.likes - 1 : comment.likes + 1,
          dislikes: wasDisliked ? comment.dislikes - 1 : comment.dislikes
        };
      }
      return comment;
    }));
  };

  const handleDislike = (commentId: string) => {
    // TODO: Implement Firebase dislike functionality
    setComments(comments.map(comment => {
      if (comment.id === commentId) {
        const wasLiked = comment.isLiked;
        const wasDisliked = comment.isDisliked;
        
        return {
          ...comment,
          isLiked: false,
          isDisliked: !wasDisliked,
          likes: wasLiked ? comment.likes - 1 : comment.likes,
          dislikes: wasDisliked ? comment.dislikes - 1 : comment.dislikes + 1
        };
      }
      return comment;
    }));
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!user) return;
    
    try {
      await deleteComment(commentId);
      toast({
        title: 'Comment Deleted',
        description: 'Your comment has been removed.',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error deleting comment:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete comment. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleDeleteClick = (commentId: string) => {
    setCommentToDelete(commentId);
    onOpen();
  };

  const confirmDelete = async () => {
    if (commentToDelete) {
      await handleDeleteComment(commentToDelete);
      setCommentToDelete(null);
      onClose();
    }
  };

  const renderComment = (comment: Comment, isReply: boolean = false) => (
    <Box key={comment.id} ml={isReply ? 8 : 0} mb={4}>
      <HStack align="start" gap={3}>
        <Avatar size="sm" src={comment.avatar} />
        <VStack align="stretch" flex="1" gap={2}>
          <HStack>
            <Text fontSize="sm" fontWeight="bold" color={textColor}>
              {comment.author}
            </Text>
            <Text fontSize="xs" color={placeholderColor}>
              {comment.timestamp}
            </Text>
            <Spacer />
            {user && comment.userId === user.uid && (
              <IconButton
                aria-label="Delete comment"
                icon={<IconWrapper icon={FiTrash2} size={16} />}
                size="xs"
                variant="ghost"
                color="red.500"
                _hover={{ color: 'red.600', bg: 'red.50' }}
                onClick={() => handleDeleteClick(comment.id)}
              />
            )}
          </HStack>
          
          <Text fontSize="sm" color={textColor} lineHeight="1.5">
            {comment.content}
          </Text>
          
          <HStack gap={4}>
            <HStack gap={1}>
              <IconButton
                aria-label="Like comment"
                icon={<IconWrapper icon={FiThumbsUp} size={14} />}
                size="xs"
                variant="ghost"
                color={comment.isLiked ? 'green.500' : placeholderColor}
                onClick={() => handleLike(comment.id)}
              />
              <Text fontSize="xs" color={placeholderColor}>
                {comment.likes}
              </Text>
            </HStack>
            
            <HStack gap={1}>
              <IconButton
                aria-label="Dislike comment"
                icon={<IconWrapper icon={FiThumbsDown} size={14} />}
                size="xs"
                variant="ghost"
                color={comment.isDisliked ? 'red.500' : placeholderColor}
                onClick={() => handleDislike(comment.id)}
              />
              <Text fontSize="xs" color={placeholderColor}>
                {comment.dislikes}
              </Text>
            </HStack>
            
            <Button
              size="xs"
              variant="ghost"
              leftIcon={<IconWrapper icon={FiCornerUpLeft} size={14} />}
              color={placeholderColor}
              _hover={{ color: 'purple.500' }}
            >
              Reply
            </Button>
          </HStack>
          
          {comment.replies.length > 0 && (
            <VStack align="stretch" gap={2} mt={2}>
              {comment.replies.map(reply => renderComment(reply, true))}
            </VStack>
          )}
        </VStack>
      </HStack>
    </Box>
  );

  if (isLoading) {
    return (
      <Box bg={bgColor} borderRadius="lg" p={6} border="1px" borderColor={borderColor}>
        <Center py={8}>
          <VStack>
            <Spinner size="lg" color="purple.500" />
            <Text color={textColor}>Loading comments...</Text>
          </VStack>
        </Center>
      </Box>
    );
  }

  return (
    <Box bg={bgColor} borderRadius="lg" p={6} border="1px" borderColor={borderColor}>
      <VStack align="stretch" gap={6}>
        <HStack>
          <Text fontSize="xl" fontWeight="bold" color={textColor}>
            Comments
          </Text>
          <Badge colorScheme="purple" variant="subtle">
            {comments.length}
          </Badge>
        </HStack>
        
        <Divider />
        
        {/* Add Comment Section */}
        {user ? (
          <VStack align="stretch" gap={3}>
            <Text fontSize="sm" fontWeight="medium" color={textColor}>
              Add a comment
            </Text>
            <Textarea
              placeholder="Share your thoughts about this content..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              bg={inputBg}
              border="none"
              color={textColor}
              _placeholder={{ color: placeholderColor }}
              _focus={{ bg: inputBg }}
              rows={3}
            />
            <HStack justify="flex-end">
              <Button
                size="sm"
                colorScheme="purple"
                onClick={handleSubmitComment}
                isDisabled={!newComment.trim() || isSubmitting}
                isLoading={isSubmitting}
                loadingText="Posting..."
              >
                Post Comment
              </Button>
            </HStack>
          </VStack>
        ) : (
          <VStack align="stretch" gap={3}>
            <Text fontSize="sm" fontWeight="medium" color={textColor}>
              Sign in to comment
            </Text>
            <Text color={placeholderColor} fontSize="sm">
              You need to be signed in to post comments and interact with the community.
            </Text>
          </VStack>
        )}
        
        <Divider />
        
        {/* Comments List */}
        <VStack align="stretch" gap={4}>
          {comments.length === 0 ? (
            <Text color={placeholderColor} textAlign="center" py={8}>
              No comments yet. Be the first to share your thoughts!
            </Text>
          ) : (
            comments.map(comment => renderComment(comment))
          )}
        </VStack>
      </VStack>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Comment
            </AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to delete this comment? This action cannot be undone.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={confirmDelete} ml={3}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default CommentSection;
